import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import RightSheet from "../../components/ui-components/RightSheet";
import BudgetBurndownChart from "./BudgetBurndownChart";
import { getBudgetSeries } from "./budgetSeriesApi";
import ForecastWaterfall from "./ForecastWaterfall";
import { humanEUR } from "./format";

// Human-readable label for each range source the backend emits (mirrors ConfidenceBadge).
const RANGE_LABEL_TEXT = {
  calibrated: "90% range",
  recent_errors: "range of recent errors",
  model: "model range",
  calibrating: "calibrating",
};

// BudgetDetailDrawer is the right-side drill-in for a single budget: its burn-down
// chart plus a Spent / Projected / Headroom / Forecast-breach summary. It mirrors
// the summary math owned by BudgetsPanel. The synthetic "Total — all cloud & AI"
// pseudo-budget (id 0, no cap) takes Spent from the response's spent_to_date and
// Projected from the series, and skips every cap-relative figure.
//
// ctaLabel names the footer action, because where the drawer opened from decides what
// the next step reads as: from the control dashboard it's "Review budget", from the
// budgets list (where the row is already in front of you) it's "Open details".
const BudgetDetailDrawer = ({ selected, period, onClose, projectionLow, projectionHigh, ctaLabel }) => {
  const open = !!selected;
  const isTotal = selected?.id === 0;

  // useQuery is called unconditionally (hooks rule); it's disabled while closed so
  // no fetch fires until a budget is selected. Total (id 0) is a valid selection.
  const { data, isError } = useQuery({
    queryKey: ["budget-series", period, selected?.id],
    queryFn: () => getBudgetSeries({ period, budgetId: selected.id }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const series = data?.series || [];
  const cap = isTotal
    ? data?.cap ?? null
    : data?.cap ?? selected?.cap ?? null;

  // Conformal projection band: prefer the per-budget values the series response
  // now carries; fall back to the props (the total/id-0 case still passes them in).
  const low = data?.projection_low ?? projectionLow;
  const high = data?.projection_high ?? projectionHigh;

  // Total: Projected = last forecast_cum.
  const lastForecastCum = series.length
    ? Number(series[series.length - 1]?.forecast_cum) || 0
    : 0;

  // Total: Spent = the response's spent_to_date — the actual total over the whole elapsed
  // window. The series is deliberately trimmed at the settle boundary so its actual and
  // forecast lines join exactly once; when the forecast table is anchored on a day that
  // already has real spend, that trim drops a day. Reading the headline off the series
  // therefore under-reported it against the summary tile. Older responses without the
  // field fall back to the last non-null actual_cum.
  const lastActualCum = (() => {
    for (let i = series.length - 1; i >= 0; i -= 1) {
      if (series[i]?.actual_cum != null) return Number(series[i].actual_cum);
    }
    return 0;
  })();
  const totalSpent =
    data?.spent_to_date != null && Number.isFinite(Number(data.spent_to_date))
      ? Number(data.spent_to_date)
      : lastActualCum;

  // Real-budget summary figures. The control dashboard passes the resolved KPI row in
  // `selected`, so those numbers win. The budgets list has no KPI row for every budget
  // (a draft or not-measurable one never reaches budget_risk), so fall back to the series
  // response — the same numbers the chart is drawing — instead of rendering €0.
  const hasRiskRow = selected?.actual != null || selected?.projected != null;
  const fromSeries = isTotal || !hasRiskRow;
  const spent = fromSeries ? totalSpent : selected.actual;
  const projected = fromSeries ? lastForecastCum : selected.projected;
  const spentPct = cap ? Math.round(((spent || 0) / cap) * 100) : null;
  const projPct =
    selected?.projected_pct != null
      ? Math.round(Number(selected.projected_pct) * 100)
      : cap
        ? Math.round(((projected || 0) / cap) * 100)
        : null;
  const headroom = Math.max((cap || 0) - (projected || 0), 0);
  const isOver = (projected || 0) > (cap || 0);

  // Footer CTA turns the insight into a next step. The Total pseudo-budget (id 0)
  // isn't a real budget, so it gets an "All budgets" link instead of "Review budget".
  const footer = isTotal ? (
    <Link
      to="/budgets"
      className="text-sm font-medium text-primary hover:text-primary-hover"
    >
      All budgets →
    </Link>
  ) : (
    <Link
      to={`/budgets/${selected?.id}`}
      className="block w-full rounded-ctrl bg-primary px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-primary-hover"
    >
      {ctaLabel} →
    </Link>
  );

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      title={selected?.name}
      subtitle={period}
      footer={selected ? footer : null}
    >
      {isError ? (
        <div className="py-6 text-center text-sm text-text-3">
          Couldn&apos;t load the burn-down chart.
        </div>
      ) : (
        <BudgetBurndownChart
          cap={cap}
          series={series}
          projectionLow={low}
          projectionHigh={high}
        />
      )}

      {/* Owner affordance: surface who owns this budget. Quiet/muted, no action —
          the concrete next step is the "Review budget" footer CTA. Total has no owner. */}
      {!isTotal && (
        <div
          className="mt-4 text-xs text-text-3"
          title={selected?.owner || undefined}
        >
          Owner: <span className="text-text-2">{selected?.owner || "—"}</span>
        </div>
      )}

      {/* Summary grid: Spent / Projected month-end / Headroom / Forecast breach.
          Mirrors BudgetsPanel. Total has no cap → no %, Headroom/breach show "—". */}
      <div className="mt-5 grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-text-3">
            Spent to date
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-text">
            {isTotal ? (
              humanEUR(spent)
            ) : (
              <>
                {humanEUR(spent)}{" "}
                {spentPct != null && (
                  <span className="font-normal text-text-3">({spentPct}%)</span>
                )}
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-text-3">
            Projected month-end
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-text">
            {isTotal ? (
              humanEUR(projected)
            ) : (
              <>
                {humanEUR(projected)}{" "}
                {projPct != null && (
                  <span className="font-normal text-text-3">({projPct}%)</span>
                )}
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-text-3">
            Headroom
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-text">
            {isTotal ? (
              <span className="text-text-3">—</span>
            ) : isOver ? (
              <span className="text-danger">
                over by {humanEUR((projected || 0) - (cap || 0))}
              </span>
            ) : (
              humanEUR(headroom)
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-text-3">
            Forecast breach
          </div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-text">
            {/* No risk row ⇒ breach was never evaluated for this budget; "—" says we
                don't know, where "none this month" would claim we checked. */}
            {isTotal || !hasRiskRow ? (
              <span className="text-text-3">—</span>
            ) : selected?.days_to_breach != null ? (
              <span className="text-danger">in {selected.days_to_breach}d</span>
            ) : (
              <span className="text-text-3">none this month</span>
            )}
          </div>
        </div>
      </div>
      {isTotal && (
        <div className="mt-3 text-xs text-text-3">no total budget set</div>
      )}

      {/* Conformal projection range + coverage. Cold-start budgets (no calibration
          history) carry no band/confidence yet → a quiet "warming up" fallback.
          range_label drives the caption; coverage shown only when show_coverage. */}
      {Number(low) > 0 && Number(high) > 0 ? (
        <div className="mt-3 text-xs text-text-3">
          {RANGE_LABEL_TEXT[data?.confidence?.range_label] || "90% range"}{" "}
          {humanEUR(low)}–{humanEUR(high)}
          {data?.confidence?.show_coverage && data?.confidence?.coverage != null
            ? ` · coverage ${Math.round(data.confidence.coverage * 100)}% (n=${data.confidence.n})`
            : ""}
        </div>
      ) : (
        <div className="mt-3 text-xs text-text-3">
          Projection calibration warming up.
        </div>
      )}

      {/* Series-level cold-start (and adaptive-calibration) provisional label:
          the borrowed prior dominates the point, so the range is honest-but-young.
          range_label === "provisional" drives it; provisional_reason names the cause
          ("based on your other sources" for cold-start). */}
      {data?.confidence?.range_label === "provisional" ? (
        <div className="mt-1 text-xs text-warning">
          Provisional{data.confidence.provisional_reason ? ` — ${data.confidence.provisional_reason}` : ""}
        </div>
      ) : null}

      {/* Per-budget breach probability: shown when the series response carries
          confidence.breach_probability (coherent intervals from MinT reconciliation).
          0 is a valid low probability; the backend omits the field when unavailable. */}
      {data?.confidence?.breach_probability != null && (
        <div className="mt-2 text-xs text-text-3">
          ~{Math.round(data.confidence.breach_probability * 100)}% chance of exceeding this budget by month-end
          {data.confidence.expected_overage != null
            ? ` · ~${humanEUR(data.confidence.expected_overage)} expected overage`
            : ""}
        </div>
      )}

      {/* Forecast decomposition waterfall ("why is it €X?"). Renders nothing when
          the series response carries no decomposition (flag off / cold-start). The
          driver-lens pointer is wired for when driver_linked/source_key land in the
          payload — absent in v1, so the link simply doesn't render yet. */}
      <ForecastWaterfall
        data={data?.decomposition}
        driverLensHref={
          data?.driver_linked
            ? `/forecast/lab?source=${encodeURIComponent(data?.source_key || "")}`
            : null
        }
      />
    </RightSheet>
  );
};

BudgetDetailDrawer.propTypes = {
  selected: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    cap: PropTypes.number,
    actual: PropTypes.number,
    projected: PropTypes.number,
    projected_pct: PropTypes.number,
    days_to_breach: PropTypes.number,
    owner: PropTypes.string,
  }),
  period: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  projectionLow: PropTypes.number,
  projectionHigh: PropTypes.number,
  // Footer CTA wording; the link target is always the budget's detail page.
  ctaLabel: PropTypes.string,
};

BudgetDetailDrawer.defaultProps = {
  selected: null,
  period: undefined,
  projectionLow: null,
  projectionHigh: null,
  ctaLabel: "Review budget",
};

export default BudgetDetailDrawer;
